import { useState } from 'react'
import { StarFilledIcon, StarIcon } from '@mote/assets/icons'
import type { MoteActionButtonProps } from '../MoteActionButton'
import { MoteActionButton } from '../MoteActionButton'
import type { MoteSaveToMotebookDialogProps } from '../MoteSaveToMotebookDialog'
import { MoteSaveToMotebookDialog } from '../MoteSaveToMotebookDialog'

export interface MoteSaveToMotebookButtonProps
  extends Pick<MoteSaveToMotebookDialogProps, 'onSave' | 'title'>,
    Pick<MoteActionButtonProps, 'textPosition' | 'size' | 'forceTooltip'> {
  inMotebook: boolean
  hasHitMotebookLimit: () => boolean
  getCanUseMotebook: () => boolean
  textOverwrite?: string
}

export const MoteSaveToMotebookButton = (
  props: MoteSaveToMotebookButtonProps
): JSX.Element => {
  const [showDialog, setShowDialog] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const disabled =
    !props.getCanUseMotebook() ||
    (props.getCanUseMotebook() &&
      props.hasHitMotebookLimit() &&
      !props.inMotebook)

  function getText() {
    if (props.textOverwrite) {
      return props.textOverwrite
    }

    let text = 'Save to motebook (Upgrade to access)'

    if (props.getCanUseMotebook()) {
      if (props.hasHitMotebookLimit() && !props.inMotebook)
        text = 'Motebook is full. Please remove old motes to add more.'
      else if (props.inMotebook) text = 'Remove from motebook'
      else text = 'Save to motebook'
    }

    return text
  }

  async function handleRemoveFromMotebook(): Promise<void> {
    setIsSaving(true)
    try {
      await props.onSave(props.title)
    } catch {}
    setIsSaving(false)
  }

  return (
    <>
      <MoteActionButton
        {...{
          onClick: !props.inMotebook
            ? () => setShowDialog(true)
            : handleRemoveFromMotebook,
          loading: isSaving,
          disabled,
          size: props.size,
          text: getText(),
          icon: props.inMotebook ? (
            <StarFilledIcon color='orange.l1' />
          ) : (
            <StarIcon />
          ),
          id: 'save-mote-button',
          ariaLabel: getText(),
          textPosition: props.textPosition,
          forceTooltip: props.forceTooltip
        }}
      />
      {!props.inMotebook && (
        <MoteSaveToMotebookDialog
          title={props.title}
          onSave={props.onSave}
          isOpen={showDialog}
          onClose={() => setShowDialog(false)}
        />
      )}
    </>
  )
}
